import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import {
  MapPin,
  Menu,
  X,
  LogOut,
  User,
  ChevronDown,
  Settings,
  Heart,
  Shield,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const Header = () => {
  const location = useLocation();
  const { toast } = useToast();
  const { isAuthenticated, user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const dropdownRef = useRef(null);
  const headerRef = useRef(null);

  // Close dropdown and mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Navigation items based on authentication status
  const publicNavigation = [
    { name: "Doctors", href: "/doctors" },
    { name: "Pharmacy", href: "/pharmacy" },
    { name: "Lab", href: "/lab" },
    { name: "Physiotherapy", href: "/physiotherapy" },
    { name: "Nutrition", href: "/nutrition" },
    { name: "Insurance", href: "/insurance" },
    { name: "Blog", href: "/blogs" },
    { name: "Privacy", href: "/privacy", highlight: true },
  ];

  const privateNavigation = [
    { name: "Doctors", href: "/doctors" },
    { name: "Pharmacy", href: "/pharmacy" },
    { name: "Lab", href: "/lab" },
    { name: "Physiotherapy", href: "/physiotherapy" },
    { name: "Nutrition", href: "/nutrition" },
    { name: "Insurance", href: "/insurance" },
    { name: "Blog", href: "/blogs" },
    { name: "Privacy", href: "/privacy", highlight: true },
    { name: "Vitals", href: "/vitals" },
    { name: "Account", href: "/account" },
  ];

  const navigation = isAuthenticated ? privateNavigation : publicNavigation;

  const isActive = (path) => location.pathname === path;

  const handleLogoutClick = () => {
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = () => {
    // Show success toast first
    toast({
      title: "Logged out successfully",
      description: "You have been signed out of your account.",
      duration: 3000,
    });

    // Clear all user-related localStorage items
    localStorage.removeItem("access_token");
    localStorage.removeItem("token_type");
    localStorage.removeItem("user_first_name");
    localStorage.removeItem("user_name");
    localStorage.removeItem("user_id");
    localStorage.removeItem("user_roles");
    localStorage.removeItem("geeth365_user");

    // Call logout to update auth context
    logout();
    setShowLogoutConfirm(false);
  };

  const handleCancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const UserDropdown = () => (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="flex items-center space-x-2 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg px-3 py-1.5 transition-all duration-200"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        <div className="w-6 h-6 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-sm">
          <User className="w-3 h-3 text-white" />
        </div>
        <span className="text-sm font-medium text-gray-700 max-w-20 truncate">
          Hi, {user?.first_name || user?.name || "User"}
        </span>
        <ChevronDown
          className={`w-3 h-3 text-gray-500 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""
            }`}
        />
      </button>

      {isDropdownOpen && (
        <div className="absolute right-0 top-10 mt-1 w-64 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50 animate-in fade-in-0 zoom-in-95">
          {/* User Info Section */}
          <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-amber-50 rounded-t-xl">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {user?.first_name || user?.name || "User"}
            </p>
            <p className="text-xs text-gray-500 mt-1 truncate">
              {user?.email || "Welcome to GEETH365"}
            </p>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <Link
              to="/account"
              className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 transition-colors duration-200"
              onClick={() => setIsDropdownOpen(false)}
            >
              <User className="w-4 h-4 mr-3 text-orange-500" />
              My Account
            </Link>
            <Link
              to="/vitals"
              className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 transition-colors duration-200"
              onClick={() => setIsDropdownOpen(false)}
            >
              <Heart className="w-4 h-4 mr-3 text-orange-500" />
              Health Vitals
            </Link>
            {user?.roles?.includes("admin") && (
              <Link
                to="/admin"
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 transition-colors duration-200"
                onClick={() => setIsDropdownOpen(false)}
              >
                <Shield className="w-4 h-4 mr-3 text-orange-500" />
                Admin
              </Link>
            )}
            <button
              disabled
              className="flex items-center w-full px-4 py-2.5 text-sm text-gray-400 cursor-not-allowed transition-colors duration-200"
            >
              <Settings className="w-4 h-4 mr-3 text-gray-300" />
              Profile Settings
            </button>
          </div>

          {/* Logout Section */}
          <div className="border-t border-gray-100 pt-1">
            <button
              className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200 rounded-b-xl"
              onClick={handleLogoutClick}
            >
              <LogOut className="w-4 h-4 mr-3" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <header ref={headerRef} className="bg-white shadow-sm border-b sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo - Centered on mobile, left on desktop */}
          <div className="flex items-center justify-center flex-1 md:flex-none">
            <Link to="/" className="flex items-center space-x-3 group">
              <img
                src="https://customer-assets.emergentagent.com/job_healthcare-hub-25/artifacts/e5l587sq_GEETH365.jpg"
                alt="GEETH365 Logo"
                className="w-10 h-10 rounded-full border-2 border-orange-200 group-hover:border-orange-400 transition-colors duration-300 object-cover shadow-sm"
              />
              <div className="flex flex-col">
                <span className="text-xl font-bold text-gray-900 group-hover:text-orange-600 transition-colors duration-300">
                  GEETH365
                </span>
                <span className="text-xs text-orange-600 font-medium">
                  Healthcare
                </span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 ${isActive(item.href)
                  ? "bg-gradient-to-r from-orange-100 to-amber-100 text-orange-700 shadow-sm"
                  : item.highlight
                    ? "text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-blue-200 hover:shadow-sm"
                    : "text-gray-600 hover:text-orange-600 hover:bg-orange-50 hover:shadow-sm"
                  }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Location & Auth Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              className="text-orange-600 border-orange-200 hover:bg-orange-50 hover:shadow-sm transition-all duration-300 h-9 px-3"
            >
              <MapPin className="w-4 h-4 mr-1" />
              530041
            </Button>

            {isAuthenticated ? (
              <UserDropdown />
            ) : (
              <Link to="/auth">
                <Button
                  size="sm"
                  className="bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white shadow-sm hover:shadow-md transition-all duration-300 h-9"
                >
                  Sign Up Free
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-600 hover:bg-orange-50 transition-colors duration-300"
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t bg-white py-4 animate-in slide-in-from-top duration-300">
            <div className="space-y-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2.5 rounded-md text-base font-medium transition-all duration-300 ${isActive(item.href)
                    ? "bg-gradient-to-r from-orange-100 to-amber-100 text-orange-700 shadow-sm"
                    : item.highlight
                      ? "text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-blue-200"
                      : "text-gray-600 hover:text-orange-600 hover:bg-orange-50"
                    }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-3 space-y-2">
                <Button
                  variant="outline"
                  className="w-full text-orange-600 border-orange-200 hover:bg-orange-50 transition-colors duration-300"
                >
                  <MapPin className="w-4 h-4 mr-1" />
                  Change Location
                </Button>

                {isAuthenticated ? (
                  <div className="space-y-2">
                    <div className="text-center text-sm text-gray-600 py-2 bg-orange-50 rounded-lg">
                      Welcome, {user?.first_name || user?.name}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Link
                        to="/account"
                        className="block"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Button
                          variant="outline"
                          className="w-full text-orange-600 border-orange-200"
                        >
                          <User className="w-4 h-4 mr-1" />
                          Account
                        </Button>
                      </Link>
                      <Button
                        onClick={handleLogoutClick}
                        variant="outline"
                        className="w-full text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <LogOut className="w-4 h-4 mr-1" />
                        Logout
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Link
                    to="/auth"
                    className="block"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Button className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white">
                      Sign Up Free
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* WhatsApp Support Strip */}
      <div className="bg-gradient-to-r from-orange-600 to-amber-600 text-white py-2 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <a
            href="https://wa.me/917337541113?text=Hello GEETH365, I need help with healthcare services"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium hover:text-orange-200 transition-colors duration-300 flex items-center justify-center space-x-2"
          >
            <span className="text-lg">📱</span>
            <span>Need Help? Chat on WhatsApp - 7337541113</span>
          </a>
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      {showLogoutConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4 animate-in fade-in-0 duration-300"
          onClick={handleCancelLogout}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <LogOut className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Sign Out
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Are you sure you want to sign out of your account?
                </p>
              </div>
            </div>

            <div className="flex space-x-3 justify-end mt-6">
              <Button
                variant="outline"
                onClick={handleCancelLogout}
                className="border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors duration-200 px-6"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmLogout}
                className="bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white shadow-sm hover:shadow-md transition-all duration-200 px-6"
              >
                Yes, Sign Out
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
